import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import CustomerLayout from '../../components/layout/CustomerLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import {
  Plus, CalendarDays, Clock, Pause, Play, X, RefreshCcw, Package,
  Search, ArrowLeft, Minus, Sparkles, Filter
} from 'lucide-react';
import { format, addDays } from 'date-fns';
import axios from 'axios';
import { takeLoginIntent } from '../../utils/loginIntent';
import { storeOffersSubscriptions } from '../../utils/subscriptionEligibility';
import { withoutPromotion } from '../../utils/promotions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCY_LABELS = {
  daily: 'Daily',
  alternate: 'Alternate Days',
  weekly: 'Weekly',
  twice_weekly: 'Twice a Week',
  monthly: 'Monthly',
  fortnightly: 'Once in 15 Days',
  quarterly: 'Quarterly',
  half_yearly: 'Half Yearly',
  yearly: 'Yearly'
};

const CustomerSubscriptions = () => {
  const { storeSettings } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const [subscriptions, setSubscriptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [deliverySlots, setDeliverySlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pauseDate, setPauseDate] = useState(null);
  const [selectedSubForPause, setSelectedSubForPause] = useState(null);

  // Flow state: 'list' -> 'catalog' -> 'configure'
  const [step, setStep] = useState('list');
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogCategory, setCatalogCategory] = useState(null);

  // Subscription config form
  const [newSub, setNewSub] = useState({
    product_id: '',
    quantity: 1,
    frequency: 'daily',
    delivery_slot_id: '',
    start_date: addDays(new Date(), 1),
    payment_mode: 'postpaid'
  });

  useEffect(() => {
    takeLoginIntent('/subscriptions'); // a guest sent to log in from a product card has now arrived
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [subRes, prodRes, slotRes, catRes] = await Promise.all([
        axios.get(`${API}/customer/subscriptions`),
        axios.get(`${API}/customer/products`),
        axios.get(`${API}/admin/delivery-slots`),
        axios.get(`${API}/admin/categories`).catch(() => ({ data: [] }))
      ]);
      // Subscriptions are charged regular prices — promotions apply to one-time
      // orders only — so show those, not the catalogue's promotional prices.
      const eligible = prodRes.data.filter(p => p.is_subscription_available).map(withoutPromotion);
      setSubscriptions(subRes.data);
      setProducts(eligible);
      setDeliverySlots(slotRes.data);
      setCategories(catRes.data);
      if (slotRes.data.length > 0) {
        setNewSub(prev => ({ ...prev, delivery_slot_id: slotRes.data[0].id }));
      }
      // Arrived from a product card's "Subscribe" (?product=<id>): go
      // straight to setting up that product — only if it really is one of the
      // subscribable products. The parameter is used once, then dropped.
      const wanted = searchParams.get('product');
      if (wanted) {
        const product = eligible.find(p => p.id === wanted);
        if (product) handleSelectProduct(product);
        setSearchParams({}, { replace: true });
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally { setLoading(false); }
  };

  const handleSelectProduct = (product) => {
    setSelectedProduct(product);
    setNewSub(prev => ({ ...prev, product_id: product.id }));
    setStep('configure');
  };

  const handleCreateSubscription = async () => {
    if (!newSub.product_id) { toast.error('Please select a product'); return; }
    if (!newSub.delivery_slot_id) { toast.error('Please select a delivery slot'); return; }
    try {
      await axios.post(`${API}/customer/subscriptions`, {
        ...newSub,
        start_date: format(newSub.start_date, 'yyyy-MM-dd')
      });
      toast.success('Subscription created!');
      setStep('list');
      setSelectedProduct(null);
      setCatalogSearch('');
      setCatalogCategory(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create subscription');
    }
  };

  const handlePauseDate = async (subscriptionId, date) => {
    try {
      await axios.put(`${API}/customer/subscriptions/${subscriptionId}/pause`, null, {
        params: { date: format(date, 'yyyy-MM-dd') }
      });
      toast.success('Delivery paused for ' + format(date, 'dd MMM'));
      setSelectedSubForPause(null);
      setPauseDate(null);
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to pause delivery'); }
  };

  const handleResumeDate = async (subscriptionId, date) => {
    try {
      await axios.put(`${API}/customer/subscriptions/${subscriptionId}/resume`, null, { params: { date } });
      toast.success('Delivery resumed');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to resume delivery'); }
  };

  const handleCancelSubscription = async (subscriptionId) => {
    if (!window.confirm('Are you sure you want to cancel this subscription?')) return;
    try {
      await axios.put(`${API}/customer/subscriptions/${subscriptionId}/cancel`);
      toast.success('Subscription cancelled');
      loadData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to cancel subscription'); }
  };

  const currencySymbol = storeSettings?.currency_symbol || '₹';
  const allowedFrequencies = storeSettings?.allowed_frequencies || Object.keys(FREQUENCY_LABELS);
  // Starting a subscription needs the store to have subscriptions switched on
  // — the server refuses otherwise. This page can still be opened without the
  // Subscribe tab (by its address, or an old link), so it must not offer one
  // then; whatever subscriptions the customer already has stay manageable.
  const canStartSubscription = storeOffersSubscriptions(storeSettings);
  const storeHasSubscriptionsOff = !!storeSettings && !canStartSubscription;

  const formatTime = (time) => {
    if (!time) return '';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    return `${h % 12 || 12}:${minutes} ${h >= 12 ? 'PM' : 'AM'}`;
  };

  // ── STEP: PRODUCT CATALOG ──
  if (step === 'catalog' && canStartSubscription) {
    const filteredCatalog = products.filter(p => {
      const matchCat = !catalogCategory || p.category_id === catalogCategory;
      const matchSearch = !catalogSearch || p.name.toLowerCase().includes(catalogSearch.toLowerCase());
      return matchCat && matchSearch;
    });

    return (
      <CustomerLayout title="Choose Product" showBottomNav={false}>
        <div className="flex flex-col h-[calc(100vh-4rem)]">
          {/* Header with back */}
          <div className="px-4 py-3 bg-[var(--theme-surface)] border-b border-black/5 sticky top-[60px] z-30">
            <div className="flex items-center gap-3 mb-3">
              <button
                onClick={() => { setStep('list'); setCatalogSearch(''); setCatalogCategory(null); }}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100"
                data-testid="catalog-back-btn"
              >
                <ArrowLeft className="w-5 h-5 text-[var(--theme-text-main)]" />
              </button>
              <h2 className="font-heading text-lg font-bold text-[var(--theme-text-main)]">
                Select a Product to Subscribe
              </h2>
            </div>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--theme-text-muted)]" />
              <input
                type="text"
                placeholder="Search subscription products..."
                value={catalogSearch}
                onChange={e => setCatalogSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-black/10 text-sm outline-none focus:border-[var(--theme-primary)]"
                data-testid="catalog-search-input"
              />
            </div>
          </div>

          <div className="flex flex-1 overflow-hidden">
            {/* Category sidebar */}
            {categories.length > 0 && (
              <div className="w-20 bg-[var(--theme-surface)] border-r border-black/5 overflow-y-auto">
                <div className="py-2">
                  <button
                    onClick={() => setCatalogCategory(null)}
                    className={`w-full py-2.5 px-1 flex flex-col items-center gap-1 border-l-3 transition-all ${
                      !catalogCategory ? 'border-l-[3px] border-[var(--theme-primary)] bg-[var(--theme-primary)]/5' : 'border-l-[3px] border-transparent'
                    }`}
                    data-testid="catalog-category-all"
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${!catalogCategory ? 'bg-[var(--theme-primary)] text-white' : 'bg-gray-100'}`}>
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <span className="text-[10px] font-medium text-center">All</span>
                  </button>
                  {categories.map(cat => (
                    <button
                      key={cat.id}
                      onClick={() => setCatalogCategory(cat.id)}
                      className={`w-full py-2.5 px-1 flex flex-col items-center gap-1 transition-all ${
                        catalogCategory === cat.id ? 'border-l-[3px] border-[var(--theme-primary)] bg-[var(--theme-primary)]/5' : 'border-l-[3px] border-transparent'
                      }`}
                      data-testid={`catalog-category-${cat.id}`}
                    >
                      <div className={`w-10 h-10 rounded-lg overflow-hidden ${catalogCategory === cat.id ? 'ring-2 ring-[var(--theme-primary)]' : ''}`}>
                        {cat.image_url ? (
                          <img src={cat.image_url} alt={cat.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-400">
                            {cat.name.charAt(0)}
                          </div>
                        )}
                      </div>
                      <span className="text-[10px] font-medium text-center line-clamp-2">{cat.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Product grid */}
            <div className="flex-1 overflow-y-auto p-3">
              {filteredCatalog.length === 0 ? (
                <div className="text-center py-12">
                  <Filter className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-[var(--theme-text-muted)]">No subscription products found</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {filteredCatalog.map(product => {
                    const discount = product.mrp > product.price
                      ? Math.round((1 - product.price / product.mrp) * 100)
                      : 0;
                    return (
                      <div
                        key={product.id}
                        className="bg-[var(--theme-surface)] rounded-2xl border border-black/5 p-3 relative"
                        data-testid={`catalog-product-${product.id}`}
                      >
                        {discount > 0 && (
                          <div className="absolute top-2 left-2 bg-red-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded z-10">
                            {discount}% OFF
                          </div>
                        )}
                        <div className="aspect-square bg-gray-50 rounded-xl mb-2 overflow-hidden">
                          {product.image_url ? (
                            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-3xl bg-[var(--theme-primary)]/10">
                              {product.name.charAt(0)}
                            </div>
                          )}
                        </div>
                        <h4 className="font-medium text-sm text-[var(--theme-text-main)] line-clamp-2 mb-1">{product.name}</h4>
                        <p className="text-xs text-[var(--theme-text-muted)] mb-2">{product.unit}</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <span className="font-bold text-sm text-[var(--theme-text-main)]">{currencySymbol}{product.price}</span>
                            {discount > 0 && (
                              <span className="text-xs text-[var(--theme-text-muted)] line-through ml-1">{currencySymbol}{product.mrp}</span>
                            )}
                          </div>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => handleSelectProduct(product)}
                          className="w-full mt-2 bg-[var(--theme-primary)] text-white rounded-full h-8 text-xs font-semibold"
                          data-testid={`subscribe-product-${product.id}`}
                        >
                          Subscribe
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      </CustomerLayout>
    );
  }

  // ── STEP: CONFIGURE SUBSCRIPTION ──
  if (step === 'configure' && selectedProduct && canStartSubscription) {
    const discount = selectedProduct.mrp > selectedProduct.price
      ? Math.round((1 - selectedProduct.price / selectedProduct.mrp) * 100) : 0;

    return (
      <CustomerLayout title="Configure Subscription" showBottomNav={false}>
        <div className="p-4 space-y-5">
          {/* Back */}
          <button
            onClick={() => setStep('catalog')}
            className="flex items-center gap-2 text-[var(--theme-primary)] font-medium text-sm"
            data-testid="config-back-btn"
          >
            <ArrowLeft className="w-4 h-4" /> Change Product
          </button>

          {/* Selected Product Card */}
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5 flex gap-4" data-testid="selected-product-card">
            <div className="w-20 h-20 rounded-xl overflow-hidden bg-gray-50 shrink-0">
              {selectedProduct.image_url ? (
                <img src={selectedProduct.image_url} alt={selectedProduct.name} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-2xl bg-[var(--theme-primary)]/10">
                  {selectedProduct.name.charAt(0)}
                </div>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-bold text-[var(--theme-text-main)] text-base">{selectedProduct.name}</h3>
              <p className="text-xs text-[var(--theme-text-muted)]">{selectedProduct.unit}</p>
              <div className="flex items-center gap-2 mt-1">
                <span className="font-bold text-lg text-[var(--theme-primary)]">{currencySymbol}{selectedProduct.price}</span>
                {discount > 0 && (
                  <span className="text-xs text-[var(--theme-text-muted)] line-through">{currencySymbol}{selectedProduct.mrp}</span>
                )}
              </div>
            </div>
          </div>

          {/* Quantity */}
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
            <label className="text-sm font-medium mb-3 block text-[var(--theme-text-main)]">Quantity</label>
            <div className="flex items-center gap-5">
              <button
                onClick={() => setNewSub(prev => ({ ...prev, quantity: Math.max(1, prev.quantity - 1) }))}
                className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center hover:bg-gray-50"
                data-testid="qty-decrease"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="text-2xl font-bold w-8 text-center" data-testid="qty-value">{newSub.quantity}</span>
              <button
                onClick={() => setNewSub(prev => ({ ...prev, quantity: prev.quantity + 1 }))}
                className="w-10 h-10 rounded-full border border-black/10 flex items-center justify-center hover:bg-gray-50"
                data-testid="qty-increase"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Frequency */}
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
            <label className="text-sm font-medium mb-3 block text-[var(--theme-text-main)]">Delivery Frequency</label>
            <div className="flex flex-wrap gap-2">
              {allowedFrequencies.map(freq => (
                <button
                  key={freq}
                  onClick={() => setNewSub(prev => ({ ...prev, frequency: freq }))}
                  className={`px-3 py-2 rounded-full border text-xs font-medium transition-all ${
                    newSub.frequency === freq
                      ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)] text-white'
                      : 'border-black/10 text-[var(--theme-text-muted)] hover:border-[var(--theme-primary)]/50'
                  }`}
                  data-testid={`freq-${freq}`}
                >
                  {FREQUENCY_LABELS[freq] || freq}
                </button>
              ))}
            </div>
          </div>

          {/* Delivery Slot */}
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
            <label className="text-sm font-medium mb-3 block text-[var(--theme-text-main)]">Delivery Slot</label>
            <Select
              value={newSub.delivery_slot_id}
              onValueChange={value => setNewSub(prev => ({ ...prev, delivery_slot_id: value }))}
            >
              <SelectTrigger data-testid="slot-select">
                <SelectValue placeholder="Choose delivery time" />
              </SelectTrigger>
              <SelectContent>
                {deliverySlots.map(slot => (
                  <SelectItem key={slot.id} value={slot.id}>
                    <span className="block">{slot.name} ({formatTime(slot.start_time)} - {formatTime(slot.end_time)})</span>
                    <span className="block text-xs text-muted-foreground">
                      Cut-off {formatTime(slot.cut_off_time || slot.start_time)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Start Date */}
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
            <label className="text-sm font-medium mb-3 block text-[var(--theme-text-main)]">Start Date</label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full justify-start h-10" data-testid="start-date-btn">
                  <CalendarDays className="mr-2 h-4 w-4" />
                  {format(newSub.start_date, 'EEE, dd MMM yyyy')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0">
                <Calendar
                  mode="single"
                  selected={newSub.start_date}
                  onSelect={date => date && setNewSub(prev => ({ ...prev, start_date: date }))}
                  disabled={date => { const tomorrow = addDays(new Date(), 1); tomorrow.setHours(0,0,0,0); return date < tomorrow; }}
                />
              </PopoverContent>
            </Popover>
          </div>

          {/* Payment Mode */}
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5">
            <label className="text-sm font-medium mb-3 block text-[var(--theme-text-main)]">Payment Mode</label>
            <div className="grid grid-cols-2 gap-3">
              {['prepaid', 'postpaid'].map(mode => (
                <button
                  key={mode}
                  onClick={() => setNewSub(prev => ({ ...prev, payment_mode: mode }))}
                  className={`p-3 rounded-xl border-2 transition-all text-center ${
                    newSub.payment_mode === mode
                      ? 'border-[var(--theme-primary)] bg-[var(--theme-primary)]/5'
                      : 'border-black/5 hover:border-[var(--theme-primary)]/30'
                  }`}
                  data-testid={`payment-${mode}`}
                >
                  <span className="font-medium capitalize text-sm">{mode}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Summary & Submit */}
          <div className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-[var(--theme-primary)]/20">
            <p className="text-xs text-[var(--theme-text-muted)] mb-2">Estimated per delivery</p>
            <p className="text-xl font-bold text-[var(--theme-primary)]">
              {currencySymbol}{(selectedProduct.price * newSub.quantity).toFixed(2)}
            </p>
            <p className="text-xs text-[var(--theme-text-muted)] mt-1">
              {newSub.quantity} × {selectedProduct.name} • {FREQUENCY_LABELS[newSub.frequency]}
            </p>
          </div>

          <Button
            onClick={handleCreateSubscription}
            className="w-full bg-[var(--theme-primary)] text-white rounded-full h-12 text-base font-semibold"
            data-testid="create-subscription-btn"
          >
            Start Subscription
          </Button>
        </div>
      </CustomerLayout>
    );
  }

  // ── STEP: SUBSCRIPTION LIST (DEFAULT) ──
  return (
    <CustomerLayout title="Subscriptions">
      <div className="p-4 space-y-4">
        {/* Header with Add Button */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-[var(--theme-text-main)]">My Subscriptions</h2>
            <p className="text-sm text-[var(--theme-text-muted)]">Manage your recurring deliveries</p>
          </div>
          {canStartSubscription && (
            <Button
              onClick={() => setStep('catalog')}
              className="bg-[var(--theme-primary)] text-white rounded-full"
              data-testid="new-subscription-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New
            </Button>
          )}
        </div>

        {/* Subscriptions List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2].map(i => (
              <div key={i} className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5 animate-pulse">
                <div className="flex gap-4">
                  <div className="w-16 h-16 bg-gray-200 rounded-xl" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-1/2 mb-2" />
                    <div className="h-4 bg-gray-100 rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : subscriptions.length === 0 ? (
          <div className="text-center py-12">
            <RefreshCcw className="w-16 h-16 text-[var(--theme-text-muted)] mx-auto mb-4 opacity-30" />
            <p className="text-[var(--theme-text-muted)]">No active subscriptions</p>
            {canStartSubscription ? (
              <>
                <p className="text-sm text-[var(--theme-text-muted)] mt-1">Start a subscription for hassle-free daily deliveries</p>
                <Button
                  onClick={() => setStep('catalog')}
                  className="mt-4 bg-[var(--theme-primary)] text-white rounded-full"
                  data-testid="empty-new-subscription-btn"
                >
                  <Plus className="w-4 h-4 mr-1" /> Browse Products
                </Button>
              </>
            ) : storeHasSubscriptionsOff && (
              <p className="text-sm text-[var(--theme-text-muted)] mt-1" data-testid="subscriptions-unavailable">
                Subscriptions are not available for this store.
              </p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {subscriptions.map(sub => {
              const product = products.find(p => p.id === sub.product_id);
              return (
                <div key={sub.id} className="bg-[var(--theme-surface)] rounded-2xl p-4 border border-black/5" data-testid={`subscription-${sub.id}`}>
                  <div className="flex gap-4">
                    <div className="w-16 h-16 bg-gray-50 rounded-xl overflow-hidden flex-shrink-0">
                      {product?.image_url ? (
                        <img src={product.image_url} alt={sub.product_name} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-2xl bg-[var(--theme-primary)]/10">
                          {sub.product_name?.charAt(0) || 'P'}
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-[var(--theme-text-main)]">{sub.product_name}</h4>
                          <p className="text-sm text-[var(--theme-text-muted)]">
                            {sub.quantity} × {FREQUENCY_LABELS[sub.frequency] || sub.frequency}
                          </p>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          sub.is_paused ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'
                        }`}>
                          {sub.is_paused ? 'Paused' : 'Active'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mt-2 text-xs text-[var(--theme-text-muted)]">
                        <Clock className="w-3 h-3" />
                        <span>{sub.delivery_slot_name}</span>
                        <span>•</span>
                        <span>Next: {sub.next_delivery_date || 'N/A'}</span>
                      </div>
                      {sub.paused_dates && sub.paused_dates.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1">
                          {sub.paused_dates.map(date => (
                            <span key={date} className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 text-xs px-2 py-0.5 rounded-full">
                              {date}
                              <button onClick={() => handleResumeDate(sub.id, date)}>
                                <X className="w-3 h-3" />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 mt-4 pt-4 border-t border-black/5">
                    {/* Pause Day */}
                    {selectedSubForPause === sub.id ? (
                      <div className="w-full space-y-3">
                        <p className="text-sm font-medium text-[var(--theme-text-main)]">Select date to skip:</p>
                        <Calendar
                          mode="single"
                          selected={pauseDate}
                          onSelect={setPauseDate}
                          disabled={date => { const tomorrow = addDays(new Date(), 1); tomorrow.setHours(0,0,0,0); return date < tomorrow; }}
                        />
                        <div className="flex gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setSelectedSubForPause(null); setPauseDate(null); }} className="flex-1 rounded-full">
                            Cancel
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => pauseDate && handlePauseDate(sub.id, pauseDate)}
                            disabled={!pauseDate}
                            className="flex-1 bg-[var(--theme-primary)] text-white rounded-full"
                          >
                            Confirm Pause
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <Button
                          variant="outline" size="sm"
                          className="flex-1 rounded-full"
                          onClick={() => setSelectedSubForPause(sub.id)}
                          data-testid={`pause-btn-${sub.id}`}
                        >
                          <Pause className="w-4 h-4 mr-1" /> Pause Day
                        </Button>
                        <Button
                          variant="outline" size="sm"
                          className="flex-1 rounded-full text-red-600 border-red-200 hover:bg-red-50"
                          onClick={() => handleCancelSubscription(sub.id)}
                          data-testid={`cancel-btn-${sub.id}`}
                        >
                          <X className="w-4 h-4 mr-1" /> Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Subscription Benefits */}
        {!storeHasSubscriptionsOff && (
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-2xl p-4 border border-amber-200">
          <h3 className="font-heading font-bold text-[var(--theme-text-main)] mb-2">Why Subscribe?</h3>
          <ul className="space-y-2 text-sm text-[var(--theme-text-muted)]">
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
              Never run out of essentials
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
              Pause anytime - flexible scheduling
            </li>
            <li className="flex items-center gap-2">
              <span className="w-5 h-5 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-xs">✓</span>
              Cancel anytime - no commitments
            </li>
          </ul>
        </div>
        )}
      </div>
    </CustomerLayout>
  );
};

export default CustomerSubscriptions;
